import * as Sentry from "@sentry/react";

import {
  useFormState,
  useStoredAdviceCart,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import { ADVICE_ABOUT_YOU_STEP, ADVICE_YOUR_LIFE_SITUATION_STEP } from "..";

import { Paragraph } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { CitizenshipPepFormElements } from "Components/Form/CommonFormElements/CitizenshipPepFormElement";
import { validationSchema } from "Components/Form/CommonFormElements/CitizenshipPepFormElement/validation";
import { NavigationButtons } from "Components/NavigationButtons";
import { RedirectErrorPage } from "Components/RedirectErrorPage";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { usePartnerId } from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { nullableFormBooleanToNullableBoolean } from "Utils/formUtils";
import { isAkademikerneTheme } from "Utils/partnerUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React from "react";
import { useDispatch } from "react-redux";
import { ProductBackendName } from "../../productTypes";
import { AdviceProductDetailsFormValues } from "../ProductDetails/FormElements/interfaces";
import { useExistingAdviceCart } from "../hooks";
import { AdviceProductIntroFormValues } from "./interfaces";

export const ProductIntro = (): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const dispatch = useDispatch();
  const partnerId = usePartnerId();
  const { getText } = useTexts();
  const initialFormState = useFormState<AdviceProductIntroFormValues>(
    ADVICE_ABOUT_YOU_STEP.id,
    initialFormValues,
  );
  const akaAdviceToggle = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_754_AKA_ADVICE,
  );

  useExistingAdviceCart();
  useStoredAdviceCart();

  const handleSubmit = (form: AdviceProductIntroFormValues): void => {
    const isStayingInNorwayForMoreThanFiveYears =
      form.isScandinavianCitizen === BooleanFormType.TRUE
        ? undefined
        : nullableFormBooleanToNullableBoolean(
            form.isStayingInNorwayForMoreThanFiveYears,
          );

    dispatch(
      updatePriceCalculationFormState({
        stepId: ADVICE_ABOUT_YOU_STEP.id,
        formState: form,
      }),
    );
    dispatch(
      updatePriceCalculationFormState({
        stepId: ADVICE_YOUR_LIFE_SITUATION_STEP.id,
        formState: {
          isStayingInNorwayForMoreThanFiveYears,
        } as AdviceProductDetailsFormValues,
      }),
    );
    dispatch(nextStep());
  };

  if (
    !isAdvisor &&
    !akaAdviceToggle &&
    isAkademikerneTheme(partnerId as Partner)
  ) {
    return <RedirectErrorPage />;
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("location", "CheckPrice - ProductIntro - ADVICE");
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.ADVICE}
        validationSchema={validationSchema(
          !isAkademikerneTheme(partnerId as Partner),
        )}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AdviceProductIntroFormValues>) => {
          return (
            <Form>
              <Paragraph>{getText("pageingress.advice.aboutyou")}</Paragraph>
              <CitizenshipPepFormElements formProps={formProps} />
              <PartnerProductIntroForms />
              {!isAdvisor && <MarketingConsent />}
              <ScrollToFirstError />
              <NavigationButtons hidePreviousButton />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

export const initialFormValues = {
  isScandinavianCitizen: undefined,
  isStayingInNorwayForMoreThanFiveYears: undefined,
  pep: undefined,
};
